import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import LocalShippingIcon from "@material-ui/icons/LocalShipping"
import SaveIcon from "@material-ui/icons/Save"
import { useSnackbar } from "notistack"
import React, { useEffect } from "react"
import { ParametrosDeliveryActionsTypes } from "../context/parametrosDeliveryActions"
import { useParametrosDeliveryContext } from "../context/useParametrosDeliveryContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "1100px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        fontWeight: 600,
        padding: "10px 32px",
        boxShadow: "0 4px 12px rgba(var(--cor-botao-salvar-rgb),0.3)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)", boxShadow: "0 6px 16px rgba(var(--cor-botao-salvar-rgb),0.4)" },
    },
    checkboxLabel: {
        color: "#2c3e50",
    },
}))

export function FormularioParametrosDelivery() {
    const classes = useStyles()
    const { parametros, dispatch, salvar, erroCarregamento } = useParametrosDeliveryContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erroCarregamento])

    const handleCampo = (
        campo: keyof typeof parametros,
        valor: string | number | boolean | null
    ) => {
        dispatch({
            type: ParametrosDeliveryActionsTypes.ATUALIZAR_CAMPO,
            payload: { campo, valor },
        })
    }

    const handleSalvar = async () => {
        if (parametros.pedidoMinimo === null || parametros.pedidoMinimo === undefined) {
            alertStart("Valor Pedido Mínimo é obrigatório.", "error")
            return
        }
        try {
            await salvar()
            alertStart("Parâmetros do delivery salvos com sucesso!", "success")
        } catch (e) {
            alertStart((e as Error).message || "Erro ao salvar", "error")
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Configuração de Delivery
                </Typography>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <LocalShippingIcon className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Parâmetros do Delivery
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Valor Pedido Mínimo (R$)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, max: 999999, step: "0.01" }}
                                    value={parametros.pedidoMinimo ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("pedidoMinimo", v === "" ? 0 : parseFloat(v))
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Valor Mínimo para Retirada no Local (R$)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, max: 999999, step: "0.01" }}
                                    value={parametros.valorMinimoRetiradaNoLocal ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo(
                                            "valorMinimoRetiradaNoLocal",
                                            v === "" ? null : parseFloat(v)
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Valor Máximo para Retirada no Local (R$)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, max: 999999, step: "0.01" }}
                                    value={parametros.valorMaximoRetiradaNoLocal ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo(
                                            "valorMaximoRetiradaNoLocal",
                                            v === "" ? null : parseFloat(v)
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Valor Máximo para Pagamento Online (R$)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, max: 9999999, step: "0.01" }}
                                    value={parametros.valorMaximoPagamentoOnline ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo(
                                            "valorMaximoPagamentoOnline",
                                            v === "" ? null : parseFloat(v)
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Tempo para Retirada no Local"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    inputProps={{ maxLength: 15 }}
                                    value={parametros.tempoRetiradaLocal ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("tempoRetiradaLocal", v === "" ? null : v)
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    select
                                    label="Possui delivery?"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={parametros.delivery}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("delivery", parseInt(v))
                                    }}
                                >
                                    <MenuItem value={1}>Sim</MenuItem>
                                    <MenuItem value={0}>Não</MenuItem>
                                </TextField>
                            </Grid>

                            {parametros.delivery === 1 && (
                                <>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            label="Mínimo para Entrega Grátis (R$)"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            type="number"
                                            inputProps={{ min: 1, max: 9999999, step: "0.01" }}
                                            value={parametros.minimoEntregaGratis ?? ""}
                                            onChange={e => {
                                                const v = e.target.value
                                                handleCampo(
                                                    "minimoEntregaGratis",
                                                    v === "" ? null : parseFloat(v)
                                                )
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            label="Tempo de Entrega"
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            inputProps={{ maxLength: 15 }}
                                            value={parametros.tempoEntregaTexto ?? ""}
                                            onChange={e => {
                                                const v = e.target.value
                                                handleCampo(
                                                    "tempoEntregaTexto",
                                                    v === "" ? null : v
                                                )
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            className={classes.checkboxLabel}
                                            control={
                                                <Checkbox
                                                    checked={parametros.entregaGratis}
                                                    onChange={e => {
                                                        const checked = e.target.checked
                                                        handleCampo("entregaGratis", checked)
                                                    }}
                                                    style={{ color: "#4caf50" }}
                                                />
                                            }
                                            label="FRETE GRATIS para todos os pedidos"
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            className={classes.checkboxLabel}
                                            control={
                                                <Checkbox
                                                    checked={
                                                        parametros.considerarTaxaEntregaPedidoMinimo
                                                    }
                                                    onChange={e => {
                                                        const checked = e.target.checked
                                                        handleCampo(
                                                            "considerarTaxaEntregaPedidoMinimo",
                                                            checked
                                                        )
                                                    }}
                                                    style={{ color: "#4caf50" }}
                                                />
                                            }
                                            label="Considerar taxa de entrega no pedido mínimo"
                                        />
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    className={classes.checkboxLabel}
                                    control={
                                        <Checkbox
                                            checked={parametros.permiteRetiradaBalcao}
                                            onChange={e => {
                                                const checked = e.target.checked
                                                handleCampo("permiteRetiradaBalcao", checked)
                                            }}
                                            style={{ color: "#4caf50" }}
                                        />
                                    }
                                    label="Retirada no Local"
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    className={classes.checkboxLabel}
                                    control={
                                        <Checkbox
                                            checked={parametros.permiteFormaPagamentoMesa}
                                            onChange={e => {
                                                const checked = e.target.checked
                                                handleCampo("permiteFormaPagamentoMesa", checked)
                                            }}
                                            style={{ color: "#4caf50" }}
                                        />
                                    }
                                    label="Exibir Pagamento Pedido de Mesa"
                                />
                            </Grid>
                        </Grid>

                        <Box mt={4} display="flex" justifyContent="center">
                            <Button
                                className={classes.saveButton}
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSalvar}
                            >
                                Salvar
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
